import { assertExists } from '@blocksuite/global/utils';
import type { AffineEditorContainer } from '@blocksuite/presets';
import { EditorLoading } from '@pique/component/page-detail-skeleton';
import { assertDocLoaded, type LoadedBlockSuiteDoc } from '@toeverything/infra';
import { use } from 'foxact/use';
import type { CSSProperties, ReactElement, Ref } from 'react';
import { memo, Suspense, useCallback, useEffect, useRef } from 'react';

import { BlocksuiteEditorContainer } from './blocksuite-editor-container';
import { NoPageRootError } from './no-page-error';
import { materializeLoadedDocRoot, waitForLoadedDocRoot } from './page-root';

export type ErrorBoundaryProps = {
  onReset?: () => void;
};

export type EditorProps = {
  page: LoadedBlockSuiteDoc;
  sourcePageId?: string;
  mode: 'page' | 'edgeless';
  shared?: boolean;
  defaultSelectedBlockId?: string;
  onLoadEditor?: (editor: AffineEditorContainer) => () => void;
  style?: CSSProperties;
  className?: string;
};

function usePageRoot(page: LoadedBlockSuiteDoc) {
  assertDocLoaded(page);

  if (!materializeLoadedDocRoot(page)) {
    use(
      waitForLoadedDocRoot(page, 20 * 1000).then(ready => {
        if (!ready) {
          throw new NoPageRootError(page);
        }
      })
    );
  }

  return page.root;
}

function BlockSuiteEditorImpl({
  ref,
  mode,
  page,
  sourcePageId,
  className,
  defaultSelectedBlockId,
  onLoadEditor,
  shared,
  style,
}: EditorProps & { ref?: Ref<AffineEditorContainer> }) {
  usePageRoot(page);
  assertExists(page, 'page should not be null');
  const editorDisposeRef = useRef<() => void>(() => {});
  const editorRef = useRef<AffineEditorContainer | null>(null);

  const onRefChange = useCallback(
    (editor: AffineEditorContainer | null) => {
      editorRef.current = editor;
      if (ref) {
        if (typeof ref === 'function') {
          ref(editor);
        } else {
          (ref as React.RefObject<AffineEditorContainer | null>).current =
            editor;
        }
      }
      if (editor && onLoadEditor) {
        editorDisposeRef.current = onLoadEditor(editor);
      }
    },
    [onLoadEditor, ref]
  );

  useEffect(() => {
    const disposable = page.slots.blockUpdated.once(() => {
      page.collection.setDocMeta(page.id, {
        updatedDate: Date.now(),
      });
    });
    return () => {
      disposable.dispose();
    };
  }, [page]);

  useEffect(() => {
    return () => {
      editorDisposeRef.current();
    };
  }, []);

  return (
    <BlocksuiteEditorContainer
      mode={mode}
      page={page}
      sourcePageId={sourcePageId}
      shared={shared}
      ref={onRefChange}
      className={className}
      style={style}
      defaultSelectedBlockId={defaultSelectedBlockId}
    />
  );
}

export const BlockSuiteEditor = memo(function BlockSuiteEditor({
  ref,
  ...props
}: EditorProps & { ref?: Ref<AffineEditorContainer> }): ReactElement {
  return (
    <Suspense fallback={<EditorLoading />}>
      <BlockSuiteEditorImpl key={props.page.id} ref={ref} {...props} />
    </Suspense>
  );
});
